import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

// ** create partner email template
export const createEmailTemplate = createAsyncThunk(
  "PartnerEmailTemplates/create-email-template",
  async (data: any, { rejectWithValue }) => {
    try {
      const result = await axios.post(
        `partner-create-email-template`,
        data,
        {
          withCredentials: true,
        },
      );
      if (result.data.Success) {
        toast.success(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return result.data;
    } catch (error: any) {
      toast.error(error.response.data.Description, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return rejectWithValue(error.response.data);
    }
  },
);

// ** update partner email template
export const updateEmailTemplate = createAsyncThunk(
  "PartnerEmailTemplates/update-email-template",
  async (
    { templateId, data }: { templateId: string; data: any },
    { rejectWithValue },
  ) => {
    try {
      const result = await axios.put(
        `partner-update-email-template/${templateId}`,
        data,
        {
          withCredentials: true,
        },
      );
      if (result.data.Success) {
        toast.success(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return result.data;
    } catch (error: any) {
      toast.error(error.response.data.Description, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return rejectWithValue(error.response.data);
    }
  },
);

// ** get partner email templates
export const getEmailTemplates = createAsyncThunk(
  "PartnerEmailTemplates/get-email-templates",
  async (createdFor: string, { rejectWithValue }) => {
    try {
      const result = await axios.get(
        `partner-get-email-templates/${createdFor}`,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** get partner email template details
export const getEmailTemplateDetails = createAsyncThunk(
  "PartnerEmailTemplates/get-email-template-details",
  async (templateId: any, { rejectWithValue }) => {
    try {
      const result = await axios.get(
        `partner-get-email-template-details/${templateId}`,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** delete partner email template
export const deleteEmailTemplate = createAsyncThunk(
  "PartnerEmailTemplates/delete-email-template",
  async (
    { templateId, data }: { templateId: string; data: any },
    { rejectWithValue },
  ) => {
    try {
      const result = await axios.put(
        `partner-delete-email-template/${templateId}`,
        data,
        {
          withCredentials: true,
        },
      );
      if (result.data.Success) {
        toast.success(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// * retrieve email content
export const getEmailTemplateContent = createAsyncThunk(
  "PartnerEmailTemplates/get-email-template-content",
  async (templateId: string, { rejectWithValue }) => {
    try {
      const result = await axios.get(
        `partner-get-email-template-content/${templateId}`,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

const PartnerEmailTemplates = createSlice({
  name: "PartnerEmailTemplates",
  initialState: {
    templates: [],
    template: {},
    loading: false,
    success: false,
    description: null,
    errors: null,
    templateContent: null,
  },
  reducers: {
    clearErrors: (state) => {
      state.errors = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ** bulk add subscription
      .addCase(createEmailTemplate.pending, (state) => {
        state.success = false;
        state.loading = true;
        state.errors = null;
        state.description = null;
      })
      .addCase(createEmailTemplate.fulfilled, (state, action) => {
        state.success = action.payload.Success;
        state.loading = false;
        state.errors = null;
        state.templates = action.payload.Data;
        state.description = action.payload.Description;
      })
      .addCase(createEmailTemplate.rejected, (state: any, action: any) => {
        state.loading = false;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
        state.errors = action.payload.Description;
      })

      // ** bulk update subscription
      .addCase(updateEmailTemplate.pending, (state) => {
        state.success = false;
        state.loading = true;
        state.errors = null;
        state.description = null;
      })
      .addCase(updateEmailTemplate.fulfilled, (state, action) => {
        state.success = action.payload.Success;
        state.loading = false;
        state.errors = null;
        state.templates = action.payload.Data;
        state.description = action.payload.Description;
      })
      .addCase(updateEmailTemplate.rejected, (state: any, action: any) => {
        state.loading = false;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
        state.errors = action.payload.Description;
      })
      // ** get all partner subscription
      .addCase(getEmailTemplates.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.errors = null;
        state.description = null;
      })
      .addCase(getEmailTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.errors = null;
        state.templates = action.payload.Data;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
      })
      .addCase(getEmailTemplates.rejected, (state: any, action: any) => {
        state.loading = false;
        state.errors = action.payload.Description;
        state.subscriptions = [];
        state.success = action.payload.Success;
        state.description = action.payload.Description;
      })
      // ** gc cancel subscription
      .addCase(getEmailTemplateDetails.pending, (state) => {
        state.loading = true;
        state.errors = null;
        state.success = false;
        state.description = null;
      })
      .addCase(getEmailTemplateDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.errors = null;
        state.template = action.payload.Data;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
      })
      .addCase(getEmailTemplateDetails.rejected, (state: any, action: any) => {
        state.loading = false;
        state.errors = action.payload.Description;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
      })
      // ** delete email template
      .addCase(deleteEmailTemplate.pending, (state) => {
        state.loading = true;
        state.errors = null;
        state.success = false;
        state.description = null;
      })
      .addCase(deleteEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.errors = null;
        state.templates = action.payload.Data;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
      })
      .addCase(deleteEmailTemplate.rejected, (state: any, action: any) => {
        state.loading = false;
        state.errors = action.payload.Description;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
      })

      // ** get email content
      .addCase(getEmailTemplateContent.pending, (state) => {
        state.loading = true;
        state.errors = null;
        state.success = false;
        state.description = null;
      })
      .addCase(getEmailTemplateContent.fulfilled, (state, action) => {
        state.loading = false;
        state.errors = null;
        state.templateContent = action.payload.Data;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
      })
      .addCase(getEmailTemplateContent.rejected, (state: any, action: any) => {
        state.loading = false;
        state.errors = action.payload.Description;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
      });
  },
});

export default PartnerEmailTemplates.reducer;
